import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AEditIconButton from '../../../common/buttons/AEditIconButton';
import FormValidator, { IFormValidator } from '../../../core/FormValidator';
import { ResourceTextApplication } from '../../../core/resources';
import { FarmingTypeEnum, OrganicProductionState, ProductionPlanQuestionType, QuestionResourceText } from '../../../interfaces/enums';
import { IFarmDetails } from '../../../interfaces/IBusinessEntities';
import {
    IFarmQuestion,
    IFarmSupplier,
    IPlanFarmBaseInfo,
    IPlanFarmBaseInfoErrors,
    IProductionPlan,
    IProductionPlanAccess,
} from '../../../interfaces/IProductionPlan';
import { flagSellerFarmsDeleted } from '../helpers/FarmSupplierHelper';
import ExternalSeller from './ExternalSeller';
import ExternalSellerDialog from './ExternalSellerDialog';
import Question from './Question/Question';
import { read } from 'fs';

export interface IBasicFarmInfoProps {
    data: IPlanFarmBaseInfo;
    setData: (data: IPlanFarmBaseInfo) => void;
    productionPlan: IProductionPlan;
    access: IProductionPlanAccess;
    farmDetails: IFarmDetails;
    validator: IFormValidator;
    errors: IPlanFarmBaseInfoErrors;
    readonly?: boolean;
}

export default function BasicFarmInfo(props: IBasicFarmInfoProps) {

    const {
        data,
        setData,
        productionPlan,
        access,
        farmDetails,
        validator,
        errors,
        readonly,
    } = props;

    const { t } = useTranslation<ResourceTextApplication[]>([
        'AnelmaCattleProductionPlan',
        'AnelmaGeneral',
    ]);

    const getShowCowsAreInCowShed = () => access.HasDairyAnimals || access.HasBreedingAnimals;

    const getShowOrganicProduction = () => {
        let qi = data.Questions.findIndex(q =>
            q.ResourceId === QuestionResourceText.OrganicFarmingPerformed.toString()
            && q.Type === ProductionPlanQuestionType.Boolean);

        return qi !== -1 && data.Questions[qi].Value === '1';
    }

    const getShowExternalSellers = () => {
        let qi = data.Questions.findIndex(q =>
            q.ResourceId === QuestionResourceText.BuysCalfsDirectlyFromOtherFarm.toString());

        return qi !== -1 && data.Questions[qi].Value === '1';
    }

    const [showCowsAreInCowShed, setShowCowsAreInCowShed] = useState<boolean>(getShowCowsAreInCowShed());
    const [showOrganicProduction, setShowOrganicProduction] = useState<boolean>(getShowOrganicProduction());
    const [showExternalSellers, setShowExternalSellers] = useState<boolean>(getShowExternalSellers());
    const [showExternalSellerDialog, setShowExternalSellerDialog] = useState<boolean>(false);

    useEffect(() => {
        setShowCowsAreInCowShed(getShowCowsAreInCowShed());
    }, [access]);

    useEffect(() => {
        let i = data.Questions.findIndex(q => q.ResourceId === QuestionResourceText.CowsAreInCowshed.toString() && q.Value);
        let updated: IFarmQuestion[] = [];

        if (showCowsAreInCowShed) {
            // set default answer if appears
            if (i === -1)
                updated = data.Questions.map((q) => {
                    if (q.ResourceId === QuestionResourceText.CowsAreInCowshed.toString())
                        return { ...q, Value: '2' }; // set added question answer default
                    return q;
                });
            // add an existing answer on load
            else
                updated = data.Questions.map((q) => {
                    if (q.ResourceId === QuestionResourceText.CowsAreInCowshed.toString())
                        return { ...q, Value: data.Questions[i].Value };
                    return q;
                });
        }
        else {
            // reset value to null, when removing question
            updated = data.Questions.map((q) => {
                if (q.ResourceId === QuestionResourceText.CowsAreInCowshed.toString())
                    return { ...q, Value: null };
                return q;
            });
        }

        setData(
            {
                ...data,
                Questions: updated,
            } as IPlanFarmBaseInfo)
    }, [showCowsAreInCowShed]);

    const onExternalSellerDialogSave = (farms: IFarmSupplier[]) => {
        setData({ ...data, SellingFarms: farms });
        setShowExternalSellers(farms.length > 0);
    };

    const isDairyFarmOnly: boolean =
        access.ProductionDirection.findIndex((d) => d === FarmingTypeEnum.Dairy) !== -1 &&
        access.ProductionDirection.findIndex((d) => d !== FarmingTypeEnum.Dairy) === -1;

    const getQuestion = (question: IFarmQuestion, error?: boolean, required?: boolean) => {
        return (
            <Question
                question={question}
                setQuestion={(question) => {
                    let updated = data.Questions.map((q) => {
                        if (q.ResourceId == question.ResourceId && q.Type == question.Type)
                            return question;
                        return q;
                    });

                    setData(
                        {
                            ...data,
                            Questions: updated,
                        } as IPlanFarmBaseInfo)
                }}
                validator={validator}
                readonly={readonly}
                error={error}
                required={required}
            />
        );
    }

    const getCowShedElement = () => {
        let i = data.Questions.findIndex(q => q.ResourceId === QuestionResourceText.CowsAreInCowshed.toString());

        if (i != -1) {
            return getQuestion(data.Questions[i]);
        }

        return <></>;
    }

    const getLimousinMembershipElement = () => {
        let i = data.Questions.findIndex(q => q.ResourceId === QuestionResourceText.LimousineMembershipExists.toString());

        if (i != -1) {
            return getQuestion(data.Questions[i]);
        }

        return <></>;
    }

    const getPaymentAccountingElement = () => {
        let i = data.Questions.findIndex(q => q.ResourceId === QuestionResourceText.PrintedClearanceRequested.toString());

        if (i != -1) {
            return getQuestion(data.Questions[i]);
        }

        return <></>;
    }

    const getOrganicFarmingPerformedElement = () => {
        let qi = data.Questions.findIndex(q =>
            q.ResourceId === QuestionResourceText.OrganicFarmingPerformed.toString()
            && q.Type === ProductionPlanQuestionType.Boolean);

        let q2i = data.Questions.findIndex(q =>
            q.ResourceId === QuestionResourceText.OrganicFarmingPerformed.toString()
            && q.Type === ProductionPlanQuestionType.SingleSelect);

        if (qi !== -1 && q2i !== -1) {
            let question = data.Questions[qi];
            let childQuestion = data.Questions[q2i];

            return (
                <Question
                    question={question}
                    setQuestion={(changes) => {
                        if (changes.Value !== '1') {
                            childQuestion = {
                                ...childQuestion,
                                Value: null,
                            } as IFarmQuestion; // have to clear child question value
                        }

                        let updated = data.Questions.map((q) => {
                            if (q.ResourceId === question.ResourceId && q.Type === question.Type)
                                return changes;
                            if (q.ResourceId === childQuestion.ResourceId && q.Type === childQuestion.Type)
                                return childQuestion;
                            return q;
                        });

                        setData(
                            {
                                ...data,
                                Questions: updated,
                            } as IPlanFarmBaseInfo
                        );

                        setShowOrganicProduction(changes.Value === '1');
                    }}
                    validator={validator}
                    readonly={readonly}
                />
            );
        }

        return <></>;
    }

    const getOrganicFarmingPerformedAdditionalInfoElement = () => {
        let i = data.Questions.findIndex(q =>
            q.ResourceId === QuestionResourceText.OrganicFarmingPerformed.toString()
            && q.Type === ProductionPlanQuestionType.SingleSelect);

        if (i != -1) {
            return getQuestion(data.Questions[i]);
        }

        return <></>;
    }

    const getBuysCalfsDirectlyFromOtherFarmElement = () => {
        let i = data.Questions.findIndex(q => q.ResourceId === QuestionResourceText.BuysCalfsDirectlyFromOtherFarm.toString());

        if (i != -1) {
            return (
                <Question
                    question={data.Questions[i]}
                    setQuestion={(question) => {
                        let updated = data.Questions.map((q) => {
                            if (q.ResourceId == question.ResourceId && q.Type == question.Type)
                                return question;
                            return q;
                        });

                        setData(
                            {
                                ...data,
                                Questions: updated,
                                SellingFarms: question.Value === '1'
                                    ? data.SellingFarms
                                    : flagSellerFarmsDeleted(data.SellingFarms)
                            } as IPlanFarmBaseInfo);
                        setShowExternalSellerDialog(question.Value === '1');
                    }}
                    validator={validator}
                    readonly={readonly}
                    error={errors.calfBuyError}
                />
            );
        }

        return <></>;
    }

    const getNewSalmonellaSampleOrderedElement = () => {
        let i = data.Questions.findIndex(q => q.ResourceId === QuestionResourceText.HasSalmonellaInsurance.toString());

        if (i != -1) {
            return getQuestion(data.Questions[i], errors.salmonellaSampleError);
        }

        return <></>;
    }

    const getSoftSleepingPadElement = () => {
        let i = data.Questions.findIndex(q => q.ResourceId === QuestionResourceText.SoftSleepingPad.toString());

        if (i != -1) {
            return getQuestion(data.Questions[i], errors.sleepingPadError);
        }

        return <></>;
    }

    const getExternalSellers = () => {
        if (showExternalSellers)
            return <>
                {
                    data.SellingFarms
                        .filter(f => f.Flags === 0)
                        .map((_) => {
                            return (
                                <ExternalSeller
                                    key={Date.now() + Math.random()}
                                    readonly={true}
                                    validator={validator}
                                    sellerFarm={_}
                                    handleSellerFarmsChange={(change: IFarmSupplier) => { }}
                                />
                            );
                        })
                }
            </>

        return <></>;
    };

    const getPlanToIncreaseAnimalAmountElement = () => {
        let i = data.Questions.findIndex(q => q.ResourceId === QuestionResourceText.PlanToIncreaseAnimalAmount.toString());

        if (i != -1) {
            return (
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                    {getQuestion(data.Questions[i])}
                </Grid>
            );
        }

        return <></>;
    }

    return (
        <Grid container direction='row' style={{ paddingLeft: 40, paddingTop: '20px' }}>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <Typography variant='h4'>{t('AnelmaLayout:1070')}</Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                {showCowsAreInCowShed &&
                    getCowShedElement()
                }

                {getLimousinMembershipElement()}

                {getPaymentAccountingElement()}

                {getOrganicFarmingPerformedElement()}
            </Grid>

            {showOrganicProduction && (
                <Grid
                    size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                    style={{ border: errors.organicProductionError ? 'solid red' : 'none' }}
                >
                    {getOrganicFarmingPerformedAdditionalInfoElement()}
                </Grid>
            )}

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                {!isDairyFarmOnly && (
                    <AEditIconButton
                        onClick={() => setShowExternalSellerDialog(true)}
                        tooltip={t('AnelmaGeneral:1075')}
                        style={{ float: 'right', paddingTop: 0 }}
                    />
                )}
                {!isDairyFarmOnly && 
                    getBuysCalfsDirectlyFromOtherFarmElement()
                }
            </Grid>

            {getExternalSellers()}

            {productionPlan.TargetYear > 2024 && (
                <Grid
                    size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                    style={{ border: errors.salmonellaSampleError ? 'solid red' : 'none' }}
                >
                    {getNewSalmonellaSampleOrderedElement()}
                </Grid>
            )}

            {productionPlan.TargetYear > 2024 && (
                <Grid
                    size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                    style={{ border: errors.sleepingPadError ? 'solid red' : 'none' }}
                >
                    {getSoftSleepingPadElement()}
                </Grid>
            )}

            {getPlanToIncreaseAnimalAmountElement()}

            {showExternalSellerDialog ? (
                <ExternalSellerDialog
                    readonly={readonly}
                    validator={new FormValidator()}
                    onClose={() => setShowExternalSellerDialog(false)}
                    onSave={(_: IFarmSupplier[]) => onExternalSellerDialogSave(_)}
                    onCancel={(_: IFarmSupplier[]) => setShowExternalSellerDialog(false)}
                    sellerFarms={data.SellingFarms}
                    farmDetails={farmDetails}
                    questions={data.SellingFarmsQuestions}
                />
            ) : null}
        </Grid>
    );
}
